/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { cx } from '../utils'

export interface CollapseSectionProps {
  title: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
  className?: string
  'data-testid'?: string
}

const isFormControl = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement || target instanceof HTMLLabelElement || target instanceof HTMLButtonElement

/**
 * A daisyUI `collapse` on a native `<details>`. Form controls placed in the title
 * (a checkbox, say) keep their own click and do not toggle the section; clicking
 * the rest of the summary still does.
 */
export const CollapseSection: React.FC<CollapseSectionProps> = ({
  title,
  defaultOpen = false,
  children,
  className,
  'data-testid': testId,
}) => (
  <details
    className={cx('collapse collapse-arrow border border-base-300 bg-base-100', className)}
    open={defaultOpen}
    data-testid={testId}
  >
    <summary className="collapse-title min-h-0 py-2 pr-10">
      <span
        onClick={(e) => {
          if (isFormControl(e.target)) e.stopPropagation()
        }}
      >
        {title}
      </span>
    </summary>
    <div className="collapse-content">{children}</div>
  </details>
)

export default CollapseSection
